import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { reaction } from "mobx";
import { withRouter } from "react-router-dom";
import agent from "../../agent";

import Paper from "@material-ui/core/Paper";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import AppBar from "@material-ui/core/AppBar";
import {
    Grid,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
} from "@material-ui/core";

import Breadcrumb from "../../components/Breadcrumb";
import Heading from "../../components/Heading";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import MainContainer from "../../components/MainContainer";

import HGSuperPayEmployees from "./HGSuperPayEmployees";
import HGSuperPayContributionHistory from "./HGSuperPayContributionHistory";
import EmployerSuperSetup from "./EmployerSuperSetup";
import HGSuperPayBillingHistory from "./HGSuperPayBillingHistory";
import HGSuperPayManageCard from "./HGSuperPayManageCard";
import MvrIntroductionDialog from "../../components/superPay/MvrIntroductionDialog";

const HOT_PINK = "#fe0d9d";

// Feature flag: when true, hides all "Default Setting" UI and treats every venue
// as always having custom setting enabled. Set to false to restore the full toggle flow.
const HIDE_DEFAULT_SETTING = true;

function mapEmployerStatus(status) {
    if (!status || !status.onboarded) return { employerRegistered: false };
    const d = status.employerData || {};
    const src = status.employerData ? d : status;
    const addr = d.address || {};
    const contact = d.contact || {};
    const contactName = contact.name || {};
    const meta = d.superApiMeta || {};
    const debit = (meta.paymentDetails && meta.paymentDetails.directDebit && meta.paymentDetails.directDebit.bankAccount) || {};
    const refund = meta.refundAccount || {};
    const sigPerson = meta.contactPerson || {};
    const ddAgreement = meta.directDebitAgreement || {};
    const loadedSig = src.digitalSignature || "";
    const loadedSigType = src.signatureType || status.signatureType || "draw";
    return {
        employerRegistered: true,
        companyName: src.employerName || d.name || "",
        abn: src.abn || "",
        contactFirstName: src.contactFirstName || contactName.firstName || "",
        contactLastName: src.contactLastName || contactName.lastName || "",
        contactEmail: src.contactEmail || (contact.email && contact.email.value) || "",
        contactMobile: src.contactMobile || (contact.phoneNumber && contact.phoneNumber.value) || "",
        contactAreaCode: src.contactAreaCode || src.areaCode || sigPerson.areaCode || "",
        contactTelephone: src.contactPhone || sigPerson.telephone || "",
        signatoryName: src.signatoryName || sigPerson.signatoryName || status.signatoryName || "",
        signatoryPosition: src.signatoryPosition || sigPerson.signatoryPosition || status.signatoryPosition || "",
        regStreet: src.addressLine1 || addr.addressLine1 || "",
        regSuburb: src.suburb || addr.suburb || "",
        regState: src.state || addr.state || "",
        regPostcode: src.postcode || addr.postcode || "",
        contribBSB: src.bankBsb || debit.bsb || "",
        contribAccountNumber: src.bankAccountNumber || debit.accountNumber || "",
        contribAccountName: src.bankAccountName || debit.accountName || "",
        refundBSB: src.refundBsb || refund.bsb || "",
        refundAccountNumber: src.refundAccountNumber || refund.accountNumber || "",
        refundAccountName: src.refundAccountName || refund.accountName || "",
        directDebitAgreed: src.agreedToTerms || ddAgreement.agreedToTerms || !!status.agreedAt || false,
        sameAsContrib: src.sameAsDirectDebit !== undefined ? src.sameAsDirectDebit : false,
        signatureDataUrl: loadedSig,
        onboardedAtClearingHouse: !!(status.onboardedAtClearingHouse ?? (status.employerData && status.employerData.onboardedAtClearingHouse)),
        detailsSyncedAtClearingHouse: status.detailsSyncedAtClearingHouse ?? (status.employerData && status.employerData.detailsSyncedAtClearingHouse) ?? null,
        get verifiedAtClearingHouse() {
            const synced = this.detailsSyncedAtClearingHouse;
            return this.onboardedAtClearingHouse && (synced === "true" || synced === "NA");
        },
    };
}

const SHIMMER_CSS = "@keyframes hgShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}";
const SHIMMER_STYLE = {
    borderRadius: 6,
    background: "linear-gradient(90deg,#e9eaee 25%,#f3f4f6 50%,#e9eaee 75%)",
    backgroundSize: "200% 100%",
    animation: "hgShimmer 1.4s infinite",
};

const PAGE_STYLE = {
    background: "#f5f6f8",
    minHeight: "100vh",
};

const SECTION_CARD = {
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    border: "1px solid #eee",
    overflow: "hidden",
    marginBottom: 20,
};

const StatusCard = ({ label, value, valueColor, meta, action, loading }) => (
    <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: 20, height: "100%", boxShadow: "0 2px 6px rgba(0,0,0,0.04)" }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "#999", marginBottom: 8, fontWeight: 600 }}>
            {label}
        </div>
        {loading ? (
            <div>
                <div style={{ ...SHIMMER_STYLE, width: "55%", height: 22, marginBottom: 8 }} />
                <div style={{ ...SHIMMER_STYLE, width: "75%", height: 13 }} />
            </div>
        ) : (
            <Fragment>
                <div style={{ fontSize: 20, fontWeight: 700, color: valueColor || "#333" }}>{value}</div>
                {meta && <div style={{ fontSize: 12.5, color: "#888", marginTop: 6 }}>{meta}</div>}
                {action && <div style={{ marginTop: 14 }}>{action}</div>}
            </Fragment>
        )}
    </div>
);

const ModelChip = ({ model }) => {
    if (!model) return null;
    const isGroup = model === "group";
    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                fontWeight: 700,
                padding: "4px 12px",
                borderRadius: 999,
                background: isGroup ? "#e8f7ef" : "#e8f0fe",
                color: isGroup ? "#15a36a" : "#1a73e8",
                border: `1px solid ${isGroup ? "#b7e3cf" : "#b3cef5"}`,
            }}>
                ● {isGroup ? "Group Employer" : "Per-Venue Employer"}
            </span>
            <span style={{ fontSize: 11, color: "#aaa", whiteSpace: "nowrap" }}>
                To change, contact{" "}
                <a href="mailto:support@hotgiggity.com" style={{ color: "#aaa" }}>
                    support@hotgiggity.com
                </a>
            </span>
        </div>
    );
};

class VenueHGSuperPayHub extends Component {
    state = {
        tabValue: 0,
        setupLoading: true,
        changeModeOpen: false,
        pendingMode: null,
        changeModeLoading: false,
        confirmModelOpen: false,
        pendingModelChoice: null,
        employerRegistered: false,
        employerOnboardedAtClearingHouse: false,
        employerName: "",
        employerInitialValues: null,
        employerLoading: false,
        registrationConfirmOpen: false,
        availableOrgs: null,
        venueCustomising: HIDE_DEFAULT_SETTING,
        customiseConfirmOpen: false,
        customiseToggling: false,
        customisationCheckError: false,
        // Per-venue subscription data (level-2 orgs — each venue has its own Stripe customer)
        venueSubscription: null,
        venueSubscriptionLoading: false,
        // Cancel-subscription confirmation dialog
        cancelConfirmOpen: false,
        cancelling: false,
    };

    componentDidMount() {
        const currentProfile = this.props.profileStore.getCurrentProfile();
        if (currentProfile && ((!currentProfile.allowInvoices && !currentProfile.organisation?.[0]?.hgSuperPaySetupLevel) || (!currentProfile.isOwnOrganization && !currentProfile.allowInvoices))) {
            this.props.history.replace("/staff");
            return;
        }
        const orgId = this._resolveOrgId();
        if (!orgId || orgId === "hg-super-pay") {
            this._loadFromOrgStore();
        } else {
            this._initHub(orgId);
        }
    }

    componentWillUnmount() {
        if (this._venueReaction) {
            this._venueReaction();
            this._venueReaction = null;
        }
    }

    _setupVenueReaction() {
        if (this._venueReaction) this._venueReaction();
        this._venueReaction = reaction(
            () => this.props.venueHgSuperPayStore.selectedVenueId,
            (selectedVenueId) => {
                if (
                    this.props.venueHgSuperPayStore.employerModel === "per-venue" &&
                    selectedVenueId
                ) {
                    const isDefault = selectedVenueId === "__default__";
                    if (isDefault) {
                        // Increment sequence here too so any in-flight customisation check
                        // for the previously selected venue is discarded when it resolves.
                        const seq = (this._venueLoadSeq = (this._venueLoadSeq || 0) + 1);
                        const staffId = ((this.props.profileStore.getCurrentProfile() || {}).id || null);
                        if (staffId) {
                            this.props.hgSuperPayAddonStore.loadAddonStatusForProfile(staffId);
                            this._loadEmployerStatus(staffId, seq);
                        }
                    } else {
                        this._loadVenueWithCustomisationCheck(selectedVenueId);
                    }
                }
            }
        );
    }

    _loadVenueWithCustomisationCheck(venueId) {
        // Increment the load sequence so any in-flight responses for a prior venue
        // can detect they are stale and discard their results.
        const seq = (this._venueLoadSeq = (this._venueLoadSeq || 0) + 1);
        this.setState({ employerLoading: true, customisationCheckError: false });
        this.props.venueHgSuperPayStore.checkVenueCustomisation(venueId)
            .then(({ isCustomised }) => {
                if (this._venueLoadSeq !== seq) return; // stale — user switched venue
                const staffId = ((this.props.profileStore.getCurrentProfile() || {}).id || null);
                const effectiveId = (HIDE_DEFAULT_SETTING || isCustomised) ? venueId : staffId;
                this.setState({ venueCustomising: HIDE_DEFAULT_SETTING || isCustomised, customisationCheckError: false });
                if (effectiveId) {
                    // For per-venue orgs each venue has its own subscription — load it directly.
                    this._loadVenueSubscription(venueId, seq);
                    this._loadEmployerStatus(effectiveId);
                } else {
                    this.setState({ employerLoading: false });
                }
            })
            .catch(() => {
                if (this._venueLoadSeq !== seq) return; // stale
                // Do NOT fall back to default — show a retryable error instead so
                // users are never silently shown the wrong configuration.
                this.setState({ employerLoading: false, customisationCheckError: true });
            });
    }

    _resolveOrgId() {
        return this.props.match && this.props.match.params && this.props.match.params.orgId;
    }

    _loadFromOrgStore() {
        this.props.organisationStore.loadOrganisations().then(orgs => {
            if (!orgs || orgs.length === 0) {
                this.setState({ setupLoading: false });
                return;
            }
            if (orgs.length === 1) {
                this._initHub(orgs[0].id);
            } else {
                // Multiple orgs: let the user pick which org context to enter.
                this.setState({ setupLoading: false, availableOrgs: orgs });
            }
        }).catch(() => {
            this.setState({ setupLoading: false });
        });
    }

    _handleOrgSelect = (orgId) => {
        this.setState({ availableOrgs: null, setupLoading: true });
        this._initHub(orgId);
    };

    _initHub(orgId) {
        this.props.organisationStore.load(orgId).catch(() => {});

        const returnStatus = this.props.match && this.props.match.params && this.props.match.params.returnStatus;
        if (returnStatus === "employees") {
            this.setState({ tabValue: 2 });
        } else if (returnStatus === "checkoutcancelled") {
            this.props.templateStore.openSnackbar("Checkout cancelled — no changes were made.");
        } else if (returnStatus === "addcardcancelled") {
            this.props.templateStore.openSnackbar("Add card cancelled — no changes were made.");
            this.setState({ tabValue: 5 });
        }

        this._setupVenueReaction();

        this.props.venueHgSuperPayStore.loadForOrganisation(orgId).then(() => {
            this.setState({ setupLoading: false });
            if (returnStatus === "checkoutsuccess") {
                this.props.templateStore.openSnackbar("HG Super Pay subscription activated successfully!");
            } else if (returnStatus === "addcardsuccess") {
                this.props.templateStore.openSnackbar("Card added successfully!");
                this.setState({ tabValue: 5 });
            }
            const { selectedVenueId, employerModel, accessibleVenues } = this.props.venueHgSuperPayStore;
            if (employerModel === "per-venue") {
                const isDefault = !selectedVenueId || selectedVenueId === "__default__";
                // When Default Setting is hidden, auto-select the first venue so the hub
                // never lands in a blank/placeholder state.
                if (HIDE_DEFAULT_SETTING && isDefault && accessibleVenues && accessibleVenues.length > 0) {
                    const firstVenueId = accessibleVenues[0].id || accessibleVenues[0].profileId;
                    if (firstVenueId) {
                        this.props.venueHgSuperPayStore.setSelectedVenue(firstVenueId);
                        // The venue reaction (_setupVenueReaction) fires on selectedVenueId
                        // change and calls _loadVenueWithCustomisationCheck — no extra load needed.
                        return;
                    }
                }
                if (isDefault) {
                    const staffId = ((this.props.profileStore.getCurrentProfile() || {}).id || null);
                    if (staffId) {
                        this.props.hgSuperPayAddonStore.loadAddonStatusForProfile(staffId);
                        this._loadEmployerStatus(staffId);
                    } else {
                        this.setState({ employerLoading: false });
                    }
                } else {
                    this._loadVenueWithCustomisationCheck(selectedVenueId);
                }
            } else {
                // group mode or no model
                const profileId = this._getEffectiveProfileId();
                if (profileId) {
                    this.props.hgSuperPayAddonStore.loadAddonStatusForProfile(profileId);
                    this._loadEmployerStatus(profileId);
                } else {
                    this.setState({ employerLoading: false });
                }
            }
        }).catch(() => {
            this.setState({ setupLoading: false, employerLoading: false });
        });
    }

    // Load the subscription status for a specific venue profile (level-2 per-venue billing).
    // Results are discarded when a newer load sequence has started (stale-guard).
    _loadVenueSubscription(venueId, seq) {
        if (!venueId) return;
        this.setState({ venueSubscriptionLoading: true, venueSubscription: null });
        agent.HgSuperPayAddon.getStatus(venueId)
            .then(response => {
                if ((this._venueLoadSeq || 0) !== seq) return; // stale
                const data = response?.data || response;
                this.setState({ venueSubscriptionLoading: false, venueSubscription: data || null });
            })
            .catch(() => {
                if ((this._venueLoadSeq || 0) !== seq) return; // stale
                this.setState({ venueSubscriptionLoading: false, venueSubscription: null });
            });
    }

    // For group model, the subscription lives on the org staff profile.
    // For per-venue, each venue owns its subscription — use _loadVenueSubscription instead.
    _getAddonProfileId() {
        const currentProfile = this.props.profileStore.getCurrentProfile();
        return currentProfile ? currentProfile.id : null;
    }

    _getEffectiveProfileId() {
        const { employerModel, selectedVenueId } = this.props.venueHgSuperPayStore;
        const currentProfile = this.props.profileStore.getCurrentProfile();
        const staffId = currentProfile ? currentProfile.id : null;
        if (employerModel === "group") {
            return staffId;
        }
        if (employerModel === "per-venue") {
            // Default Setting always uses the org staff profile
            if (!selectedVenueId || selectedVenueId === "__default__") return staffId;
            // Real venue: only use venueId when the custom setting is ON
            if (this.state.venueCustomising) return selectedVenueId;
            // Custom setting is OFF — use org staff profile (Default Setting data)
            return staffId;
        }
        return null;
    }

    _loadEmployerStatus(profileId, seqOverride) {
        if (!profileId) return;
        // Use the passed seq (for default-venue path) or capture the current one.
        // This ensures results are discarded if the selection changes mid-flight.
        const seq = seqOverride !== undefined ? seqOverride : (this._venueLoadSeq || 0);
        this.setState({ employerLoading: true });
        this.props.hgSuperPayStore.getEmployerStatus(profileId)
            .then(status => {
                if ((this._venueLoadSeq || 0) !== seq) return; // stale
                const mapped = mapEmployerStatus(status);
                this.setState({
                    employerLoading: false,
                    employerRegistered: mapped.employerRegistered || false,
                    employerOnboardedAtClearingHouse: mapped.verifiedAtClearingHouse || false,
                    employerName: mapped.companyName || "",
                    employerInitialValues: mapped,
                });
            })
            .catch(() => {
                if ((this._venueLoadSeq || 0) !== seq) return; // stale
                this.setState({ employerLoading: false, employerInitialValues: { employerRegistered: false } });
            });
    }

    handleSelectModel = (model) => {
        const orgId = this.props.venueHgSuperPayStore.organisationId;
        if (!orgId) return;
        this.setState({ setupLoading: true });
        this.props.venueHgSuperPayStore.switchEmployerModel(orgId, model).then(() => {
            this.setState({ setupLoading: false });
            const addonProfileId = this._getAddonProfileId();
            if (addonProfileId) this.props.hgSuperPayAddonStore.loadAddonStatusForProfile(addonProfileId);
            const profileId = this._getEffectiveProfileId();
            if (profileId) this._loadEmployerStatus(profileId);
        }).catch(() => {
            this.setState({ setupLoading: false });
            this.props.templateStore.openSnackbar("Failed to save employer model. Please try again.");
        });
    };

    handleOpenChangeMode = () => {
        const { employerModel } = this.props.venueHgSuperPayStore;
        const next = employerModel === "group" ? "per-venue" : "group";
        this.setState({ changeModeOpen: true, pendingMode: next });
    };

    handleConfirmChangeMode = () => {
        const { pendingMode } = this.state;
        this.setState({ changeModeOpen: false, changeModeLoading: true });
        const orgId = this.props.venueHgSuperPayStore.organisationId;
        this.props.venueHgSuperPayStore.switchEmployerModel(orgId, pendingMode).then(() => {
            this.setState({ changeModeLoading: false, tabValue: 0 });
            this.props.templateStore.openSnackbar("Employer model updated successfully.");
            const addonProfileId = this._getAddonProfileId();
            if (addonProfileId) this.props.hgSuperPayAddonStore.loadAddonStatusForProfile(addonProfileId);
            const profileId = this._getEffectiveProfileId();
            if (profileId) this._loadEmployerStatus(profileId);
        }).catch(() => {
            this.setState({ changeModeLoading: false });
            this.props.templateStore.openSnackbar("Failed to update employer model. Please try again.");
        });
    };

    handleTabChange = (event, tabValue) => {
        this.setState({ tabValue });
        if (tabValue === 1) {
            const profileId = this._getEffectiveProfileId();
            if (profileId) this._loadEmployerStatus(profileId);
        }
    };

    handleVenueChange = (e) => {
        const newValue = e.target.value;
        // Reset customiseToggling so an in-flight PUT for the previous venue cannot
        // leave the new venue's toggle permanently disabled.
        // Also clear the stale venue subscription so the new venue loads fresh data.
        this.setState({
            tabValue: 0,
            venueCustomising: HIDE_DEFAULT_SETTING,
            customiseToggling: false,
            venueSubscription: null,
            venueSubscriptionLoading: false,
        });
        this.props.venueHgSuperPayStore.setSelectedVenue(newValue);
    };

    handleCustomiseToggle = (turnOn) => {
        if (turnOn) {
            // OFF → ON: persist to backend, reload with venueId
            // Capture venue at the moment the user clicks — used to guard against
            // dropdown changes while the PUT is in flight.
            const venueIdAtToggle = this.props.venueHgSuperPayStore.selectedVenueId;
            this.setState({ customiseToggling: true });
            this.props.venueHgSuperPayStore.setVenueCustomisation(venueIdAtToggle, true)
                .then(({ isCustomised }) => {
                    // Discard if the user has already switched to a different venue.
                    if (this.props.venueHgSuperPayStore.selectedVenueId !== venueIdAtToggle) return;
                    if (!isCustomised) {
                        // Backend returned false despite requesting true — treat as failure
                        this.props.templateStore.openSnackbar("Could not enable custom setting. Please try again.");
                        this.setState({ customiseToggling: false });
                        return;
                    }
                    // Reset all employer summary state before applying venue-specific data
                    // so stale Default Setting values are never inherited by the new venue.
                    this.setState({
                        venueCustomising: true,
                        tabValue: 1,
                        customiseToggling: false,
                        employerRegistered: false,
                        employerOnboardedAtClearingHouse: false,
                        employerName: "",
                        employerInitialValues: { employerRegistered: false },
                    });
                    // Addon subscription is org-level — always load with staffId.
                    const addonProfileId = this._getAddonProfileId();
                    if (addonProfileId) this.props.hgSuperPayAddonStore.loadAddonStatusForProfile(addonProfileId);
                    // Load the venue's own employer data. If not found, leave the form blank.
                    this.props.hgSuperPayStore.getEmployerStatus(venueIdAtToggle)
                        .then(status => {
                            if (this.props.venueHgSuperPayStore.selectedVenueId !== venueIdAtToggle) return;
                            const mapped = mapEmployerStatus(status);
                            this.setState({
                                employerRegistered: mapped.employerRegistered || false,
                                employerOnboardedAtClearingHouse: mapped.verifiedAtClearingHouse || false,
                                employerName: mapped.companyName || "",
                                employerInitialValues: mapped,
                                employerLoading: false,
                            });
                        })
                        .catch(() => {
                            if (this.props.venueHgSuperPayStore.selectedVenueId !== venueIdAtToggle) return;
                            // Not found or error — show a blank form.
                            this.setState({ employerInitialValues: { employerRegistered: false }, employerLoading: false });
                        });
                })
                .catch(() => {
                    this.props.templateStore.openSnackbar("Could not enable custom setting. Please try again.");
                    this.setState({ customiseToggling: false });
                });
        } else {
            // ON → OFF: show confirmation dialog first
            this.setState({ customiseConfirmOpen: true });
        }
    };

    handleCustomiseToggleConfirmOff = () => {
        // Capture venue at the moment the user confirms — guard against dropdown
        // changes while the PUT is in flight.
        const venueIdAtToggle = this.props.venueHgSuperPayStore.selectedVenueId;
        const staffId = ((this.props.profileStore.getCurrentProfile() || {}).id || null);
        this.setState({ customiseConfirmOpen: false, customiseToggling: true });
        this.props.venueHgSuperPayStore.setVenueCustomisation(venueIdAtToggle, false)
            .then(({ isCustomised }) => {
                // Discard if the user has already switched to a different venue.
                if (this.props.venueHgSuperPayStore.selectedVenueId !== venueIdAtToggle) return;
                if (isCustomised) {
                    // Backend returned true despite requesting false — treat as failure
                    this.props.templateStore.openSnackbar("Could not disable custom setting. Please try again.");
                    this.setState({ customiseToggling: false });
                    return;
                }
                this.setState({ venueCustomising: false, tabValue: 0, customiseToggling: false });
                if (staffId) {
                    this.props.hgSuperPayAddonStore.loadAddonStatusForProfile(staffId);
                    this._loadEmployerStatus(staffId);
                }
            })
            .catch(() => {
                this.props.templateStore.openSnackbar("Could not disable custom setting. Please try again.");
                this.setState({ customiseToggling: false });
            });
    };

    handleEmployerStatusChange = (registered, name) => {
        this.setState({ employerRegistered: registered, employerName: name });
    };

    handleRegister = async (formState) => {
        const profileId = this._getEffectiveProfileId();
        const { employerModel, selectedVenueId } = this.props.venueHgSuperPayStore;
        const isDefault = selectedVenueId === "__default__";
        const payload = {
            profileId,
            venueId: (employerModel === "per-venue" && !isDefault && this.state.venueCustomising) ? selectedVenueId : undefined,
            employerName: formState.companyName,
            abn: formState.abn,
            employerArrangement: "DEFAULT",
            location: "AU",
            contributionsEnabled: true,
            employeeServicesEnabled: true,
            singleTouchPayrollEnabled: false,
            splitMultiEmployerBatch: false,
            contactFirstName: formState.contactFirstName,
            contactMiddleName: "",
            contactLastName: formState.contactLastName,
            contactEmail: formState.contactEmail,
            contactMobile: formState.contactMobile,
            contactPhone: formState.contactTelephone,
            contactAreaCode: formState.contactAreaCode,
            signatoryName: formState.signatoryName,
            signatoryPosition: formState.signatoryPosition,
            addressType: "POSTAL",
            addressLine1: formState.regStreet,
            addressLine2: "",
            addressLine3: "",
            suburb: formState.regSuburb,
            postcode: formState.regPostcode,
            state: formState.regState,
            bankAccountName: formState.contribAccountName,
            bankBsb: formState.contribBSB,
            bankAccountNumber: formState.contribAccountNumber,
            sameAsDirectDebit: formState.sameAsContrib,
            refundBsb: formState.refundBSB,
            refundAccountName: formState.refundAccountName,
            refundAccountNumber: formState.refundAccountNumber,
            agreedToTerms: formState.directDebitAgreed,
            agreedAt: new Date().toISOString(),
            digitalSignature: formState.signatureDataUrl || "",
            signatureType: formState.signatureDataUrl ? "upload" : "draw",
            legalAgreements: {
                termsAndConditions: true,
                privacyPolicy: true,
                directDebitServiceAgreement: true,
                masterServiceAgreement: true,
            },
        };
        const isNewRegistration = !this.state.employerRegistered;
        await this.props.hgSuperPayStore.onboardEmployer(payload, !isNewRegistration);
        this.setState({ employerRegistered: true, employerName: formState.companyName, registrationConfirmOpen: true });
        if (profileId) this._loadEmployerStatus(profileId);
    };

    handleCancelAddon = async () => {
        const { employerModel, selectedVenueId } = this.props.venueHgSuperPayStore;
        const isPerVenueReal = employerModel === "per-venue" && selectedVenueId && selectedVenueId !== "__default__";
        this.setState({ cancelling: true });
        try {
            if (isPerVenueReal) {
                await agent.HgSuperPayAddon.cancel(selectedVenueId);
                // Reload this venue's subscription so the Overview reflects the new state.
                const seq = (this._venueLoadSeq = (this._venueLoadSeq || 0) + 1);
                this._loadVenueSubscription(selectedVenueId, seq);
            } else {
                await this.props.hgSuperPayAddonStore.cancelAddon();
            }
            this.setState({ cancelConfirmOpen: false, cancelling: false });
            this.props.templateStore.openSnackbar("HG Super Pay subscription cancelled.");
        } catch (e) {
            this.setState({ cancelling: false });
            const status = e?.response?.status || e?.status;
            const message =
                status === 404
                    ? "No active subscription was found."
                    : "Failed to cancel. Please try again.";
            this.props.templateStore.openSnackbar(message);
        }
    };

    handleActivateAddon = async () => {
        const { employerModel, selectedVenueId } = this.props.venueHgSuperPayStore;
        const isPerVenueReal = employerModel === "per-venue" && selectedVenueId && selectedVenueId !== "__default__";

        if (isPerVenueReal) {
            // Level-2 org: each venue subscribes independently.
            try {
                const response = await agent.HgSuperPayAddon.subscribe(selectedVenueId, true);
                const data = response?.data || response;
                const url = data?.checkoutUrl || data?.url;
                if (url) window.open(url, "_blank");
            } catch (e) {
                const status = e?.response?.status || e?.status;
                let message;
                if (status === 409) message = "This venue already has an active HG Super Pay subscription.";
                else if (status === 404) message = "Billing must be set up before activating this add-on.";
                else message = "Failed to start checkout. Please try again.";
                this.props.templateStore.openSnackbar(message);
            }
            return;
        }

        // Group model — subscription lives on the org staff profile.
        const profileId = this._getAddonProfileId();
        if (!profileId) return;
        try {
            const response = await this.props.hgSuperPayAddonStore.startCheckoutForProfile(profileId, true);
            if (response && response.checkoutUrl) {
                window.open(response.checkoutUrl, "_blank");
            }
        } catch (e) {
            let message;
            if (e && e.status === 409) message = "You already have an active HG Super Pay subscription.";
            else if (e && e.status === 404) message = "Billing must be set up before activating this add-on.";
            else message = "Failed to start checkout. Please try again.";
            this.props.templateStore.openSnackbar(message);
        }
    };

    renderSetupScreen() {
        return (
            <div style={{ padding: "40px 28px" }}>
                <style>{SHIMMER_CSS}</style>
                <div style={{ textAlign: "center", marginBottom: 32 }}>
                    <h3 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 700, color: "#333" }}>Choose Your Employer Model</h3>
                    <p style={{ margin: 0, fontSize: 14, color: "#888" }}>
                        How would you like to manage superannuation across your venue group?
                    </p>
                </div>
                <Grid container spacing={16} style={{ maxWidth: 720, margin: "0 auto" }}>
                    <Grid item xs={12} md={6}>
                        <div
                            style={{
                                border: "2px solid #eee",
                                borderRadius: 12,
                                padding: 28,
                                cursor: "pointer",
                                transition: "border-color 0.18s, box-shadow 0.18s",
                                background: "#fff",
                                height: "100%",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                                display: "flex",
                                flexDirection: "column",
                            }}
                            onClick={() => this.setState({ confirmModelOpen: true, pendingModelChoice: "group" })}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = HOT_PINK; e.currentTarget.style.boxShadow = "0 4px 16px rgba(254,13,157,0.12)"; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = "#eee"; e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.04)"; }}
                        >
                            <div style={{ width: 48, height: 48, borderRadius: 10, background: "#fce4f7", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, fontSize: 22 }}>🏢</div>
                            <div style={{ fontSize: 17, fontWeight: 700, color: "#333", marginBottom: 8 }}>Group Employer</div>
                            <div style={{ fontSize: 13.5, color: "#666", lineHeight: 1.6, flex: 1, marginBottom: 20 }}>
                                One employer record and one subscription covers all venues in your group. Ideal for centralised payroll management.
                            </div>
                            <button
                                style={{
                                    background: HOT_PINK,
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 6,
                                    padding: "10px 20px",
                                    fontSize: 13,
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    letterSpacing: ".04em",
                                    alignSelf: "flex-start",
                                }}
                            >
                                CHOOSE GROUP
                            </button>
                        </div>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <div
                            style={{
                                border: "2px solid #eee",
                                borderRadius: 12,
                                padding: 28,
                                cursor: "pointer",
                                transition: "border-color 0.18s, box-shadow 0.18s",
                                background: "#fff",
                                height: "100%",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                                display: "flex",
                                flexDirection: "column",
                            }}
                            onClick={() => this.setState({ confirmModelOpen: true, pendingModelChoice: "per-venue" })}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = "#1a73e8"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(26,115,232,0.12)"; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = "#eee"; e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.04)"; }}
                        >
                            <div style={{ width: 48, height: 48, borderRadius: 10, background: "#e8f0fe", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, fontSize: 22 }}>🏪</div>
                            <div style={{ fontSize: 17, fontWeight: 700, color: "#333", marginBottom: 8 }}>Per-Venue Employer</div>
                            <div style={{ fontSize: 13.5, color: "#666", lineHeight: 1.6, flex: 1, marginBottom: 20 }}>
                                Each venue manages its own employer record and subscription independently. Best for venues with separate operations.
                            </div>
                            <button
                                style={{
                                    background: "#1a73e8",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 6,
                                    padding: "10px 20px",
                                    fontSize: 13,
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    letterSpacing: ".04em",
                                    alignSelf: "flex-start",
                                }}
                            >
                                CHOOSE PER-VENUE
                            </button>
                        </div>
                    </Grid>
                </Grid>
            </div>
        );
    }

    renderVenueDropdown() {
        const { accessibleVenues, selectedVenueId } = this.props.venueHgSuperPayStore;
        if (!accessibleVenues || accessibleVenues.length === 0) return null;
        return (
            <div style={{ marginBottom: 18 }}>
                <FormControl style={{ minWidth: 280 }}>
                    <InputLabel>Select Venue</InputLabel>
                    <Select value={selectedVenueId || ""} onChange={this.handleVenueChange}>
                        {!HIDE_DEFAULT_SETTING && (
                            <MenuItem value="__default__">
                                <span style={{ fontStyle: "italic", color: "#555" }}>⚙ Default Setting</span>
                            </MenuItem>
                        )}
                        {accessibleVenues.map(v => (
                            <MenuItem key={v.id || v.profileId} value={v.id || v.profileId}>
                                {v.profileName || v.name || `Venue ${v.id || v.profileId}`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
        );
    }

    renderOverview() {
        const { addonStatus: orgAddonStatus, currentMonthUsage: orgMonthUsage, temporaryFeeAmountFormatted: orgFeeFormatted } = this.props.hgSuperPayAddonStore;
        const { employerRegistered, employerOnboardedAtClearingHouse, employerName, employerLoading, venueCustomising, venueSubscription, venueSubscriptionLoading } = this.state;
        const { employerModel, selectedVenueId, accessibleVenues } = this.props.venueHgSuperPayStore;

        // For per-venue orgs each venue has its own subscription — use its data.
        const isPerVenueReal = employerModel === "per-venue" && !!selectedVenueId && selectedVenueId !== "__default__";
        const addonStatus = isPerVenueReal
            ? (venueSubscriptionLoading ? "loading" : (venueSubscription?.subscribed ? "active" : "inactive"))
            : orgAddonStatus;
        const currentMonthUsage = isPerVenueReal ? (venueSubscription?.currentMonthUsage || 0) : orgMonthUsage;
        const temporaryFeeAmountFormatted = isPerVenueReal ? (venueSubscription?.temporaryFeeAmountFormatted || null) : orgFeeFormatted;

        const isLoading = addonStatus === "loading";
        const isActive = addonStatus === "active";

        // Derive whether this venue is using default (same logic as render)
        const isDefaultSelected = employerModel === "per-venue" && selectedVenueId === "__default__";
        const venueUsesDefault = !HIDE_DEFAULT_SETTING && (employerModel === "per-venue" && !isDefaultSelected && !venueCustomising);

        const selectedVenue = accessibleVenues && accessibleVenues.find(v => (v.id || v.profileId) === selectedVenueId);
        const venueName = selectedVenue ? (selectedVenue.profileName || selectedVenue.name || "") : "";

        const addonValue = isLoading ? "Checking…" : isActive ? "Active" : "Inactive";
        const addonColor = isLoading ? "#6b7280" : isActive ? "#15a36a" : "#6b7280";

        const showCta = employerModel === "per-venue" && selectedVenueId && (!isActive || !employerRegistered);

        return (
            <div>
                <style>{SHIMMER_CSS}</style>

                {showCta && (
                    <div style={{
                        background: "#fff8e1",
                        border: "1px solid #ffe082",
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 18,
                        color: "#8a6d00",
                        fontSize: 13.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                    }}>
                        <span>⚠️</span>
                        <span>
                            {venueName ? <strong>{venueName}</strong> : "This venue"} needs to{!isActive ? " activate the HG Super Pay add-on" : ""}{(!isActive && !employerRegistered) ? " and" : ""}{!employerRegistered ? " complete Employer Setup" : ""} before it can pay super.
                        </span>
                    </div>
                )}

                <Grid container spacing={16}>
                    <Grid item xs={12} md={4}>
                        <StatusCard
                            label="Add-on"
                            loading={isLoading}
                            value={addonValue}
                            valueColor={addonColor}
                            meta="$1.50 ex GST per submission"
                            action={
                                !isLoading && !isActive ? (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        style={{ backgroundColor: HOT_PINK, color: "#fff" }}
                                        onClick={this.handleActivateAddon}
                                    >
                                        Activate
                                    </Button>
                                ) : !isLoading && isActive ? (
                                    <Button
                                        size="small"
                                        style={{ color: "#d32f2f" }}
                                        onClick={() => this.setState({ cancelConfirmOpen: true })}
                                    >
                                        Cancel
                                    </Button>
                                ) : null
                            }
                        />
                    </Grid>
                    {isActive && (
                        <Grid item xs={12} md={4}>
                            <StatusCard
                                label="Employer"
                                loading={employerLoading}
                                value={
                                    !employerRegistered ? "Not registered"
                                    : employerOnboardedAtClearingHouse ? "Active"
                                    : "Submitted - Awaiting Verification"
                                }
                                valueColor={
                                    !employerRegistered ? "#6b7280"
                                    : employerOnboardedAtClearingHouse ? "#15a36a"
                                    : "#1976d2"
                                }
                                meta={employerRegistered ? employerName : null}
                                action={
                                    <Button
                                        size="small"
                                        disabled={venueUsesDefault}
                                        style={{ color: venueUsesDefault ? "#ccc" : HOT_PINK }}
                                        onClick={() => !venueUsesDefault && this.setState({ tabValue: 1 })}
                                        title={venueUsesDefault ? "Enable custom setting to edit employer setup" : undefined}
                                    >
                                        {!employerRegistered ? "Set up now" : "Edit"}
                                    </Button>
                                }
                            />
                        </Grid>
                    )}
                    {isActive && (
                        <Grid item xs={12} md={4}>
                            <StatusCard
                                label="This month"
                                loading={isLoading}
                                value={`${currentMonthUsage || 0} submission${(currentMonthUsage || 0) !== 1 ? "s" : ""}${temporaryFeeAmountFormatted ? ` ≈ ${temporaryFeeAmountFormatted}` : ""}`}
                                meta="Billed 5th of next month"
                            />
                        </Grid>
                    )}
                </Grid>

                <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "28px 0 14px" }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>Quick actions</span>
                    <div style={{ flex: 1, height: 1, background: "#eee" }} />
                </div>
                <Grid container spacing={16}>
                    {[
                        { title: "Super Ledger", desc: "See what super you owe and pay it", icon: "📊", onClick: () => {
                            const { employerModel, organisationId, selectedVenueId } = this.props.venueHgSuperPayStore;
                            if (employerModel === "group" && organisationId) {
                                this.props.reportStore.setOrganisationFilter(organisationId);
                            } else if (employerModel === "per-venue" && selectedVenueId) {
                                this.props.reportStore.setVenueFilter(selectedVenueId);
                            }
                            this.props.history.push("/superLedgers");
                        }},
                        { title: "Payments", desc: "Track submitted payments and their status", icon: "💳", onClick: () => {
                            const { employerModel, organisationId, selectedVenueId } = this.props.venueHgSuperPayStore;
                            if (employerModel === "group" && organisationId) {
                                this.props.reportStore.setOrganisationFilter(organisationId);
                            } else if (employerModel === "per-venue" && selectedVenueId) {
                                this.props.reportStore.setVenueFilter(selectedVenueId);
                            }
                            this.props.history.push("/payments");
                        }},
                        ...(isActive ? [{ title: "Manage employees", desc: "Map sole-trader artists to HG Super Pay", icon: "👥", onClick: () => !venueUsesDefault && this.setState({ tabValue: 2 }) }] : [])
                    ].map((qa) => (
                        <Grid item xs={12} md={4} key={qa.title}>
                            <div
                                onClick={qa.onClick}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 14,
                                    background: "#fff",
                                    border: "1px solid #eee",
                                    borderRadius: 10,
                                    padding: "16px 18px",
                                    cursor: "pointer",
                                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                                    transition: "box-shadow 0.15s",
                                }}
                                onMouseEnter={e => (e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.09)")}
                                onMouseLeave={e => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)")}
                            >
                                <span style={{ fontSize: 22 }}>{qa.icon}</span>
                                <span style={{ flex: 1 }}>
                                    <span style={{ fontWeight: 600, fontSize: 14, display: "block", color: "#333" }}>{qa.title}</span>
                                    <span style={{ fontSize: 12.5, color: "#888" }}>{qa.desc}</span>
                                </span>
                                <span style={{ color: HOT_PINK, fontSize: 18, fontWeight: 700 }}>›</span>
                            </div>
                        </Grid>
                    ))}
                </Grid>
            </div>
        );
    }

    renderNotActive() {
        return (
            <div style={{ textAlign: "center", padding: "48px 24px" }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>💤</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "#333", marginBottom: 8 }}>HG Super Pay Not Active</div>
                <div style={{ fontSize: 13.5, color: "#888", maxWidth: 360, margin: "0 auto" }}>
                    Activate the HG Super Pay add-on from the Overview tab to set up and manage superannuation payments.
                </div>
            </div>
        );
    }

    render() {
        const { tabValue, setupLoading, changeModeOpen, pendingMode, changeModeLoading, registrationConfirmOpen, venueSubscription, venueSubscriptionLoading } = this.state;
        const { addonStatus: orgAddonStatus } = this.props.hgSuperPayAddonStore;
        const { employerModel, organisationId, selectedVenueId } = this.props.venueHgSuperPayStore;
        const { canManage } = this.props.organisationStore;
        const currentProfile = this.props.profileStore.getCurrentProfile();

        // For per-venue orgs each venue has its own subscription — derive status from venue data.
        const isPerVenueReal = employerModel === "per-venue" && !!selectedVenueId && selectedVenueId !== "__default__";
        const addonStatus = isPerVenueReal
            ? (venueSubscriptionLoading ? "loading" : (venueSubscription?.subscribed ? "active" : "inactive"))
            : orgAddonStatus;

        const isLoading = addonStatus === "loading";
        const isActive = addonStatus === "active";

        const breadcrumb = [
            { name: "Manage" },
            { name: "Organisation" },
            { name: "HG Super Pay" },
        ];

        const addonBadge =
            addonStatus === "loading"
                ? { label: "Checking…", color: "#999", bg: "#f5f5f5" }
                : addonStatus === "active"
                ? { label: "Active", color: "#15a36a", bg: "#e8f7ef" }
                : { label: "Inactive", color: "#999", bg: "#f5f5f5" };

        const { availableOrgs } = this.state;

        const { venueCustomising, customiseConfirmOpen, customiseToggling, customisationCheckError } = this.state;
        const isDefaultSelected = employerModel === "per-venue" && selectedVenueId === "__default__";
        // A real venue uses the default when it has not been toggled ON for custom setting.
        // When HIDE_DEFAULT_SETTING is true this is always false — every venue is treated as custom.
        const venueUsesDefault = !HIDE_DEFAULT_SETTING && (employerModel === "per-venue" && !isDefaultSelected && !venueCustomising);
        // _getEffectiveProfileId() already accounts for venueCustomising state
        const effectiveProfileId = this._getEffectiveProfileId();
        // Show the toggle only when a real venue is selected in per-venue mode (hidden when HIDE_DEFAULT_SETTING).
        const showCustomiseToggle = !HIDE_DEFAULT_SETTING && (employerModel === "per-venue" && !!selectedVenueId && !isDefaultSelected);
        // Billing tabs are only relevant for per-venue orgs with a real venue selected —
        // each venue has its own Stripe customer (level-2 org model).
        const showBillingTabs = employerModel === "per-venue" && !!selectedVenueId && selectedVenueId !== "__default__";
        const TAB_LABELS = showBillingTabs
            ? ["Overview", "Employer Setup", "Employees", "Contribution History", "Billing History", "Manage Card"]
            : ["Overview", "Employer Setup", "Employees", "Contribution History"];
        const visibleTabCount = TAB_LABELS.length;

        if (process.env.REACT_APP_DISABLE_BILLING_MOBILE === "true") {
            return (
                <Fragment>
                    <style>{SHIMMER_CSS}</style>
                    <Header role={currentProfile && this.props.templateStore.getTypeString(currentProfile.type)} />
                    <Sidebar />
                    <MainContainer>
                        <Typography variant="display1" style={{ textAlign: "center", marginTop: 60, color: "#6b7280" }}>
                            This feature is only available on the desktop version.
                        </Typography>
                    </MainContainer>
                </Fragment>
            );
        }

        return (
            <Fragment>
                <style>{SHIMMER_CSS}</style>
                <Header role={currentProfile && this.props.templateStore.getTypeString(currentProfile.type)} />
                <Sidebar />
                <MainContainer>
                    <Breadcrumb links={breadcrumb} />
                    {currentProfile && (currentProfile.allowInvoices ||
                        (currentProfile.isOwnOrganization && currentProfile.organisation?.[0]?.hgSuperPaySetupLevel)) &&
                        <MvrIntroductionDialog />}

                    {/* Org picker — shown when user belongs to multiple orgs */}
                    {availableOrgs && (
                        <div style={{ ...SECTION_CARD, padding: 0 }}>
                            <div style={{ padding: "24px 28px", borderBottom: "1px solid #f0f0f0" }}>
                                <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: "#333" }}>Select Organisation</h2>
                                <p style={{ margin: 0, fontSize: 13.5, color: "#888" }}>
                                    Choose which organisation's HG Super Pay you want to manage.
                                </p>
                            </div>
                            <div style={{ padding: "20px 28px" }}>
                                <Grid container spacing={16}>
                                    {availableOrgs.map(org => (
                                        <Grid item xs={12} md={4} key={org.id}>
                                            <div
                                                onClick={() => this._handleOrgSelect(org.id)}
                                                style={{
                                                    background: "#fff",
                                                    border: "1px solid #eee",
                                                    borderRadius: 10,
                                                    padding: "18px 20px",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                                                    transition: "box-shadow 0.15s",
                                                }}
                                                onMouseEnter={e => (e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.09)")}
                                                onMouseLeave={e => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)")}
                                            >
                                                <span style={{ fontWeight: 600, fontSize: 14.5, color: "#333" }}>{org.name || `Organisation ${org.id}`}</span>
                                                <span style={{ color: HOT_PINK, fontSize: 18, fontWeight: 700 }}>›</span>
                                            </div>
                                        </Grid>
                                    ))}
                                </Grid>
                            </div>
                        </div>
                    )}

                    {/* Main hub content */}
                    {!availableOrgs && (
                    <Fragment>

                        {/* Page header card */}
                        <div style={{ ...SECTION_CARD }}>
                            <div style={{ padding: "24px 28px", borderBottom: employerModel ? "1px solid #f0f0f0" : "none" }}>
                                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                                    <div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                                            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#333" }}>HG Super Pay</h2>
                                            <span style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 4,
                                                fontSize: 11,
                                                fontWeight: 700,
                                                padding: "3px 10px",
                                                borderRadius: 999,
                                                background: addonBadge.bg,
                                                color: addonBadge.color,
                                                letterSpacing: ".03em",
                                            }}>
                                                ● {addonBadge.label}
                                            </span>
                                        </div>
                                        <p style={{ margin: 0, fontSize: 13.5, color: "#888" }}>
                                            Manage superannuation payments for your venue group. Australian customers only.
                                        </p>
                                    </div>
                                    {employerModel && (
                                        <ModelChip model={employerModel} />
                                    )}
                                </div>
                            </div>
                            {employerModel === "per-venue" && (
                                <div style={{ padding: "14px 28px", borderBottom: "1px solid #f0f0f0", background: "#fafbfc", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                                    {this.renderVenueDropdown()}
                                    {showCustomiseToggle && (
                                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <span style={{ fontSize: 13, color: "#555", fontWeight: 500, whiteSpace: "nowrap" }}>Custom setting</span>
                                            {/* Toggle switch */}
                                            <button
                                                onClick={() => !customiseToggling && this.handleCustomiseToggle(!venueCustomising)}
                                                disabled={customiseToggling}
                                                style={{
                                                    position: "relative",
                                                    display: "inline-block",
                                                    width: 44,
                                                    height: 24,
                                                    background: venueCustomising ? HOT_PINK : "#ccc",
                                                    borderRadius: 12,
                                                    border: "none",
                                                    cursor: customiseToggling ? "not-allowed" : "pointer",
                                                    transition: "background 0.2s",
                                                    padding: 0,
                                                    opacity: customiseToggling ? 0.6 : 1,
                                                    flexShrink: 0,
                                                }}
                                                title={venueCustomising ? "Turn off custom setting — revert to Default" : "Turn on custom setting for this venue"}
                                            >
                                                <span style={{
                                                    position: "absolute",
                                                    top: 3,
                                                    left: venueCustomising ? 23 : 3,
                                                    width: 18,
                                                    height: 18,
                                                    background: "#fff",
                                                    borderRadius: "50%",
                                                    transition: "left 0.2s",
                                                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                                                }} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Loading spinner */}
                        {setupLoading ? (
                            <div style={{ ...SECTION_CARD, padding: "60px 0", display: "flex", justifyContent: "center" }}>
                                <CircularProgress style={{ color: HOT_PINK }} />
                            </div>
                        ) : !employerModel ? (
                            /* Employer model selection */
                            <div style={{ ...SECTION_CARD }}>
                                {this.renderSetupScreen()}
                            </div>
                        ) : customisationCheckError ? (
                            /* Customisation check failed — show retryable error */
                            <div style={{ ...SECTION_CARD, padding: "60px 24px", textAlign: "center" }}>
                                <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: "#333", marginBottom: 8 }}>
                                    Could not load venue settings
                                </div>
                                <div style={{ fontSize: 13.5, color: "#888", maxWidth: 380, margin: "0 auto 24px" }}>
                                    Unable to determine whether this venue has a custom configuration. Please try again — no changes have been made.
                                </div>
                                <button
                                    onClick={() => {
                                        const { selectedVenueId } = this.props.venueHgSuperPayStore;
                                        if (selectedVenueId && selectedVenueId !== "__default__") {
                                            this._loadVenueWithCustomisationCheck(selectedVenueId);
                                        }
                                    }}
                                    style={{ background: HOT_PINK, color: "#fff", border: "none", borderRadius: 6, padding: "10px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                                >
                                    Retry
                                </button>
                            </div>
                        ) : (
                            /* Tabbed content card */
                            <div style={{ ...SECTION_CARD }}>
                                {/* Amber banner when venue uses default */}
                                {venueUsesDefault && (
                                    <div style={{
                                        background: "#fff8e1",
                                        border: "1px solid #ffe082",
                                        borderRadius: "8px 8px 0 0",
                                        padding: "12px 20px",
                                        color: "#8a6d00",
                                        fontSize: 13.5,
                                        borderBottom: "none",
                                    }}>
                                        ⚙ This venue is using the <strong>Default Setting</strong>. Toggle on "Custom setting" above to set up venue-specific configuration.
                                    </div>
                                )}
                                {/* Tab bar */}
                                <div style={{ borderBottom: "1px solid #f0f0f0", background: "#fff" }}>
                                    <div style={{ display: "flex", overflowX: "auto" }}>
                                        {Array.from({ length: visibleTabCount }, (_, idx) => {
                                            // Billing tabs (4, 5) are never disabled by venueUsesDefault
                                            const isBillingTab = idx >= 4;
                                            const disabled = !isBillingTab && venueUsesDefault;
                                            return (
                                                <button
                                                    key={TAB_LABELS[idx]}
                                                    onClick={() => !disabled && this.handleTabChange(null, idx)}
                                                    style={{
                                                        background: "none",
                                                        border: "none",
                                                        borderBottom: tabValue === idx ? `2px solid ${HOT_PINK}` : "2px solid transparent",
                                                        padding: "14px 22px",
                                                        fontSize: 13.5,
                                                        fontWeight: tabValue === idx ? 700 : 500,
                                                        color: disabled ? "#bbb" : (tabValue === idx ? HOT_PINK : "#666"),
                                                        cursor: disabled ? "not-allowed" : "pointer",
                                                        pointerEvents: disabled ? "none" : "auto",
                                                        whiteSpace: "nowrap",
                                                        transition: "color 0.15s",
                                                        marginBottom: -1,
                                                    }}
                                                >
                                                    {TAB_LABELS[idx]}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Tab content */}
                                <div style={{ padding: 24 }}>
                                    {tabValue === 0 && this.renderOverview()}

                                    {tabValue === 1 && (
                                        isActive ? (
                                            this.state.employerLoading ? (
                                                <div style={{ padding: 32, textAlign: "center", color: "#888" }}>
                                                    Loading employer details…
                                                </div>
                                            ) : (
                                                <>
                                                    {isDefaultSelected && (
                                                        <div style={{
                                                            background: "#f0f7ff",
                                                            border: "1px solid #cce0ff",
                                                            borderRadius: 8,
                                                            padding: "12px 16px",
                                                            marginBottom: 20,
                                                            fontSize: 13.5,
                                                            color: "#1a4f8a",
                                                        }}>
                                                            <strong>Default employer configuration</strong> — These details are applied to any venue that hasn't been set up individually.
                                                        </div>
                                                    )}
                                                    <EmployerSuperSetup
                                                        initialValues={this.state.employerInitialValues || { employerRegistered: false }}
                                                        onStatusChange={this.handleEmployerStatusChange}
                                                        onRegister={this.handleRegister}
                                                    />
                                                </>
                                            )
                                        ) : isLoading ? null : this.renderNotActive()
                                    )}

                                    {tabValue === 2 && (
                                        isActive ? (
                                            <HGSuperPayEmployees
                                                embedded
                                                employerRegistered={this.state.employerOnboardedAtClearingHouse}
                                                profileId={effectiveProfileId}
                                                showVenuesColumn={employerModel === "per-venue"}
                                            />
                                        ) : isLoading ? null : this.renderNotActive()
                                    )}

                                    {tabValue === 3 && (
                                        isActive ? (
                                            <HGSuperPayContributionHistory
                                                profileId={effectiveProfileId}
                                                organisationId={employerModel === "group" ? organisationId : undefined}
                                            />
                                        ) : isLoading ? null : this.renderNotActive()
                                    )}

                                    {tabValue === 4 && showBillingTabs && (
                                        <HGSuperPayBillingHistory profileId={selectedVenueId} />
                                    )}

                                    {tabValue === 5 && showBillingTabs && (
                                        <HGSuperPayManageCard profileId={selectedVenueId} isOrganisation={true} />
                                    )}
                                </div>
                            </div>
                        )}

                    {/* Confirmation dialog: turn OFF custom setting (hidden when HIDE_DEFAULT_SETTING) */}
                    {!HIDE_DEFAULT_SETTING && (
                        <Dialog open={customiseConfirmOpen} onClose={() => this.setState({ customiseConfirmOpen: false })} maxWidth="xs" fullWidth>
                            <DialogTitle style={{ backgroundColor: HOT_PINK, padding: "12px 24px" }} disableTypography>
                                <Typography variant="title" style={{ color: "#fff", fontWeight: 700 }}>
                                    Revert to Default Setting?
                                </Typography>
                            </DialogTitle>
                            <DialogContent style={{ paddingTop: 20 }}>
                                <DialogContentText>
                                    This venue will stop using its custom employer configuration and revert to the <strong>Default Setting</strong>. Your custom data is retained and can be restored by toggling custom setting back on.
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => this.setState({ customiseConfirmOpen: false })} color="default">
                                    Cancel
                                </Button>
                                <Button onClick={this.handleCustomiseToggleConfirmOff} style={{ color: HOT_PINK, fontWeight: 700 }}>
                                    Revert to Default
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}

                    {/* One-time warning dialog shown before confirming the initial employer model selection */}
                    <Dialog open={this.state.confirmModelOpen} onClose={() => this.setState({ confirmModelOpen: false, pendingModelChoice: null })} maxWidth="xs" fullWidth>
                        <DialogTitle style={{ backgroundColor: HOT_PINK, padding: "12px 24px" }} disableTypography>
                            <Typography variant="title" style={{ color: "#fff", fontWeight: 700 }}>
                                Are you sure?
                            </Typography>
                        </DialogTitle>
                        <DialogContent style={{ paddingTop: 20 }}>
                            <DialogContentText>
                                You are choosing <strong>{this.state.pendingModelChoice === "group" ? "Group Employer" : "Per-Venue Employer"}</strong> as your employer model.
                            </DialogContentText>
                            <DialogContentText style={{ marginTop: 12 }}>
                                ⚠️ <strong>This can only be done once.</strong> Once confirmed, you will need to contact{" "}
                                <a href="mailto:support@hotgiggity.com" style={{ color: HOT_PINK }}>support@hotgiggity.com</a>{" "}
                                to make any changes.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions style={{ padding: "12px 20px" }}>
                            <Button onClick={() => this.setState({ confirmModelOpen: false, pendingModelChoice: null })} style={{ color: "#888" }}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    const choice = this.state.pendingModelChoice;
                                    this.setState({ confirmModelOpen: false, pendingModelChoice: null });
                                    this.handleSelectModel(choice);
                                }}
                                style={{ backgroundColor: HOT_PINK, color: "#fff", fontWeight: 700 }}
                            >
                                Confirm
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={changeModeOpen} onClose={() => this.setState({ changeModeOpen: false })} maxWidth="sm" fullWidth>
                        <DialogTitle style={{ backgroundColor: HOT_PINK, padding: "12px 24px" }} disableTypography>
                            <Typography variant="title" style={{ color: "#fff", fontWeight: 700 }}>
                                Change Employer Model
                            </Typography>
                        </DialogTitle>
                        <DialogContent style={{ paddingTop: 20 }}>
                            <DialogContentText>
                                You are switching to <strong>{pendingMode === "group" ? "Group Employer" : "Per-Venue Employer"}</strong> mode.
                            </DialogContentText>
                            <DialogContentText style={{ marginTop: 12 }}>
                                ⚠️ <strong>Note:</strong> Existing employer records will <strong>not</strong> be automatically migrated. You may need to re-enter employer details after switching.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions style={{ padding: "12px 20px" }}>
                            <Button onClick={() => this.setState({ changeModeOpen: false })} style={{ color: "#888" }}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={this.handleConfirmChangeMode}
                                disabled={changeModeLoading}
                                style={{ backgroundColor: HOT_PINK, color: "#fff", fontWeight: 700 }}
                            >
                                {changeModeLoading ? "Switching…" : "Confirm Switch"}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={registrationConfirmOpen} onClose={() => this.setState({ registrationConfirmOpen: false })}>
                        <DialogTitle style={{ backgroundColor: HOT_PINK, padding: "12px 24px" }} disableTypography>
                            <Typography variant="title" style={{ color: "#fff", fontWeight: 700 }}>
                                Registration Submitted
                            </Typography>
                        </DialogTitle>
                        <DialogContent style={{ paddingTop: 20 }}>
                            <DialogContentText>
                                Your employer registration has been submitted. Approval can take 24–48 hours. We will email you once the process is complete.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => this.setState({ registrationConfirmOpen: false })}
                                style={{ backgroundColor: HOT_PINK, color: "#fff" }}
                                variant="contained"
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Cancel subscription confirmation dialog */}
                    <Dialog
                        open={this.state.cancelConfirmOpen}
                        onClose={() => !this.state.cancelling && this.setState({ cancelConfirmOpen: false })}
                        maxWidth="xs"
                        fullWidth
                    >
                        <DialogTitle style={{ backgroundColor: "#d32f2f", padding: "12px 24px" }} disableTypography>
                            <Typography variant="title" style={{ color: "#fff", fontWeight: 700 }}>
                                Cancel HG Super Pay Subscription?
                            </Typography>
                        </DialogTitle>
                        <DialogContent style={{ paddingTop: 20 }}>
                            <DialogContentText>
                                This will cancel the HG Super Pay add-on subscription. Super payments will stop being processed at the end of the current billing period.
                            </DialogContentText>
                            <DialogContentText style={{ marginTop: 12 }}>
                                You can reactivate at any time.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions style={{ padding: "12px 20px" }}>
                            <Button
                                onClick={() => this.setState({ cancelConfirmOpen: false })}
                                disabled={this.state.cancelling}
                                style={{ color: "#888" }}
                            >
                                Keep Subscription
                            </Button>
                            <Button
                                variant="contained"
                                onClick={this.handleCancelAddon}
                                disabled={this.state.cancelling}
                                style={{ backgroundColor: "#d32f2f", color: "#fff", fontWeight: 700 }}
                            >
                                {this.state.cancelling ? "Cancelling…" : "Yes, Cancel"}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    </Fragment>
                    )}
                </MainContainer>
            </Fragment>
        );
    }
}

export default inject(
    "profileStore",
    "templateStore",
    "hgSuperPayAddonStore",
    "hgSuperPayStore",
    "venueHgSuperPayStore",
    "organisationStore",
    "reportStore",
)(withRouter(observer(VenueHGSuperPayHub)));
